import cx from "classnames";
import { useServiceDOI } from "components/DOIHeaderSelect/useServiceDOI";
import { ICDCodingBlock } from "components/ICDCoding";
import type { ICDUpdateData } from "components/ICDCoding/icdAdapter";
import { ControlledTextArea } from "components/inputs/TextArea/ControlledTextArea";
import { useCallback, useEffect } from "react";
import type { Path } from "react-hook-form";
import { useForm } from "react-hook-form";
import { usePatchServiceDateOfInjuryByIdMutation } from "services/doi-api";
import type { CostProjectionDataType, MSADataType } from "types/DataTypes";
import { useSelector } from "utils/redux";

type Props = {
  onSave: (field: string | null, value: unknown) => Promise<void>;
  service: MSADataType | CostProjectionDataType;
};

type FormValues = {
  "service_date_of_injury.accepted_conditions": string;
  "service_date_of_injury.denied_conditions": string;
  "service_date_of_injury.accident_description": string;
  ai_allocator_notes: string;
  general_notes: string;
  warning_notes: string;
  potential_issues_notes: string;
  medication_notes: string;
  medication_history_notes: string;
  diagnostics_notes: string;
  uds_saliva_notes: string;
  medical_equipment_notes: string;
  surgeries_notes: string;
  injection_notes: string;
  therapy_notes: string;
  medical_legal_review_notes: string;
  medical_legal_notes: string;
  accident_description_notes: string;
  pre_existing_conditions_notes: string;
};

const isMSAData = (
  service: MSADataType | CostProjectionDataType,
): service is MSADataType => {
  return "ai_allocator_notes" in service;
};

/*
we have two different places for data to go

  ServiceDateOfInjury
    accident_description: string | null;
    accepted_conditions: string | null;
    denied_conditions: string | null;

  Service (MSA, etc)
    general_notes: string;
    accepted_conditions_notes: string;
    denied_conditions_notes: string;
    medical_legal_notes: string;
    potential_issues_notes: string;
    medication_notes: string;

  are associated with the service_date_of_injury and will change when the sDOI toggle in the header pill is changed
*/
const FutureMedicalClinicianNotes = ({ service, onSave }: Props) => {
  const showClinicianNotes = useSelector(
    (state) => state.drawer.showClinicianNotes,
  );

  const { selectedServiceDoi, isLoading } = useServiceDOI(service);
  const [patchServiceDateOfInjury] = usePatchServiceDateOfInjuryByIdMutation();
  const {
    control,
    formState: { errors },
    setValue,
  } = useForm<FormValues>({
    defaultValues: {
      "service_date_of_injury.accepted_conditions":
        selectedServiceDoi?.accepted_conditions || "",
      "service_date_of_injury.denied_conditions":
        selectedServiceDoi?.denied_conditions || "",
      "service_date_of_injury.accident_description":
        selectedServiceDoi?.accident_description || "",
      ai_allocator_notes: isMSAData(service)
        ? service.ai_allocator_notes || ""
        : "",
      general_notes: isMSAData(service) ? service.general_notes || "" : "",
      warning_notes: isMSAData(service) ? service.warning_notes || "" : "",
      potential_issues_notes: isMSAData(service)
        ? service.potential_issues_notes || ""
        : "",
      medication_notes: isMSAData(service)
        ? service.medication_notes || ""
        : "",
      medication_history_notes: isMSAData(service)
        ? service.medication_history_notes || ""
        : "",
      diagnostics_notes: isMSAData(service)
        ? service.diagnostics_notes || ""
        : "",
      uds_saliva_notes: isMSAData(service)
        ? service.uds_saliva_notes || ""
        : "",
      medical_equipment_notes: isMSAData(service)
        ? service.medical_equipment_notes || ""
        : "",
      surgeries_notes: isMSAData(service) ? service.surgeries_notes || "" : "",
      injection_notes: isMSAData(service) ? service.injection_notes || "" : "",
      therapy_notes: isMSAData(service) ? service.therapy_notes || "" : "",
      medical_legal_review_notes: isMSAData(service)
        ? service.medical_legal_review_notes || ""
        : "",
      medical_legal_notes: isMSAData(service)
        ? service.medical_legal_notes || ""
        : "",
      accident_description_notes: isMSAData(service)
        ? service.accident_description_notes || ""
        : "",
      pre_existing_conditions_notes: isMSAData(service)
        ? service.pre_existing_conditions_notes || ""
        : "",
    },
  });

  // update form values when selected DOI changes
  useEffect(() => {
    if (selectedServiceDoi) {
      const doiFields = {
        "service_date_of_injury.accepted_conditions":
          selectedServiceDoi.accepted_conditions,
        "service_date_of_injury.denied_conditions":
          selectedServiceDoi.denied_conditions,
        "service_date_of_injury.accident_description":
          selectedServiceDoi.accident_description,
      } as const;

      Object.entries(doiFields).forEach(([field, value]) => {
        setValue(field as Path<FormValues>, value || "");
      });
    }
  }, [selectedServiceDoi, setValue]);

  const handleFieldSave = useCallback(
    async (field: keyof FormValues, value: string) => {
      await onSave(field, value);
    },
    [onSave],
  );

  // handler for saving ICD data from ICDCodingBlock directly to the ServiceDateOfInjury
  const handleSaveIcdData = useCallback(
    async (data: ICDUpdateData) => {
      if (selectedServiceDoi?.id) {
        try {
          await patchServiceDateOfInjury({
            id: selectedServiceDoi.id.toString(),
            data,
          });
        } catch (error) {
          console.error(
            "Failed to update ServiceDateOfInjury ICD data:",
            error,
          );
        }
      }
    },
    [selectedServiceDoi?.id, patchServiceDateOfInjury],
  );

  return (
    <div
      className={cx("documents notes-msa flex jc-se", {
        open: showClinicianNotes,
      })}
    >
      <div className="documents-wrapper notes">
        <div className="swim-wrapper h-100">
          <div className="grid-layout no-gap-tb swimlanes msa-notes">
            <div className="swim-headers">Clinician Notes</div>

            <div className="swim-column">
              <ICDCodingBlock
                data={selectedServiceDoi || undefined}
                isLoading={isLoading}
                onSave={handleSaveIcdData}
              />
              <div className="data-block">
                <div className="data-block-header">Allocation Notes</div>

                <div className="grid-layout gl-1 notes-grid">
                  <ControlledTextArea
                    control={control}
                    name="ai_allocator_notes"
                    label="AI Allocator Notes"
                    errors={errors}
                    onSave={(value) =>
                      handleFieldSave("ai_allocator_notes", value.toString())
                    }
                    readOnly={true}
                    advancedEdit={true}
                    allowMinimize={true}
                  />
                  <ControlledTextArea
                    control={control}
                    name="service_date_of_injury.accepted_conditions"
                    label="Accepted Conditions"
                    errors={errors}
                    advancedEdit={true}
                    onSave={(value) =>
                      handleFieldSave(
                        "service_date_of_injury.accepted_conditions",
                        value.toString(),
                      )
                    }
                  />
                  <ControlledTextArea
                    control={control}
                    name="service_date_of_injury.denied_conditions"
                    label="Denied Conditions"
                    errors={errors}
                    advancedEdit={true}
                    onSave={(value) =>
                      handleFieldSave(
                        "service_date_of_injury.denied_conditions",
                        value.toString(),
                      )
                    }
                  />
                  <ControlledTextArea
                    control={control}
                    name="general_notes"
                    label="Treatment Notes"
                    errors={errors}
                    advancedEdit={true}
                    onSave={(value) =>
                      handleFieldSave("general_notes", value.toString())
                    }
                  />
                  <ControlledTextArea
                    control={control}
                    name="warning_notes"
                    label="Body Part Summary / Warning / Cost Mitigation"
                    errors={errors}
                    advancedEdit={true}
                    onSave={(value) =>
                      handleFieldSave("warning_notes", value.toString())
                    }
                  />
                  <ControlledTextArea
                    control={control}
                    name="potential_issues_notes"
                    label="Potential development issues/missing materials/ confirm MPH reviewed with run date"
                    errors={errors}
                    advancedEdit={true}
                    onSave={(value) =>
                      handleFieldSave(
                        "potential_issues_notes",
                        value.toString(),
                      )
                    }
                  />
                  <ControlledTextArea
                    control={control}
                    name="medication_notes"
                    label="Medication Notes"
                    errors={errors}
                    advancedEdit={true}
                    onSave={(value) =>
                      handleFieldSave("medication_notes", value.toString())
                    }
                  />
                </div>
              </div>

              <div className="data-block">
                <div className="data-block-header">Medical History Summary</div>

                <div className="grid-layout gl-1 notes-grid">
                  <ControlledTextArea
                    control={control}
                    name="medication_history_notes"
                    label="Overview"
                    errors={errors}
                    advancedEdit={true}
                    onSave={(value) =>
                      handleFieldSave(
                        "medication_history_notes",
                        value.toString(),
                      )
                    }
                  />
                  <ControlledTextArea
                    control={control}
                    name="diagnostics_notes"
                    label="Diagnostics"
                    errors={errors}
                    advancedEdit={true}
                    onSave={(value) =>
                      handleFieldSave("diagnostics_notes", value.toString())
                    }
                  />
                  <ControlledTextArea
                    control={control}
                    name="uds_saliva_notes"
                    label="UDS/Saliva (dates)"
                    errors={errors}
                    advancedEdit={true}
                    onSave={(value) =>
                      handleFieldSave("uds_saliva_notes", value.toString())
                    }
                  />
                  <ControlledTextArea
                    control={control}
                    name="medical_equipment_notes"
                    label="Durable medical equipment (date, type, discontinued date)"
                    errors={errors}
                    advancedEdit={true}
                    onSave={(value) =>
                      handleFieldSave(
                        "medical_equipment_notes",
                        value.toString(),
                      )
                    }
                  />
                  <ControlledTextArea
                    control={control}
                    name="surgeries_notes"
                    label="Surgeries performed in last 2 years"
                    errors={errors}
                    advancedEdit={true}
                    onSave={(value) =>
                      handleFieldSave("surgeries_notes", value.toString())
                    }
                  />
                  <ControlledTextArea
                    control={control}
                    name="injection_notes"
                    label="Injections (type and date) performed in last 2 years"
                    errors={errors}
                    advancedEdit={true}
                    onSave={(value) =>
                      handleFieldSave("injection_notes", value.toString())
                    }
                  />
                  <ControlledTextArea
                    control={control}
                    name="therapy_notes"
                    label="Therapy Notes"
                    errors={errors}
                    advancedEdit={true}
                    onSave={(value) =>
                      handleFieldSave("therapy_notes", value.toString())
                    }
                  />
                  <ControlledTextArea
                    control={control}
                    name="medical_legal_review_notes"
                    label="Medical legal reviews"
                    errors={errors}
                    advancedEdit={true}
                    onSave={(value) =>
                      handleFieldSave(
                        "medical_legal_review_notes",
                        value.toString(),
                      )
                    }
                  />
                  <ControlledTextArea
                    control={control}
                    name="medical_legal_notes"
                    label="Medical legal"
                    errors={errors}
                    advancedEdit={true}
                    onSave={(value) =>
                      handleFieldSave("medical_legal_notes", value.toString())
                    }
                  />
                </div>
              </div>

              <div className="data-block">
                <div className="data-block-header">Accident</div>

                <div className="grid-layout gl-1 notes-grid">
                  <ControlledTextArea
                    control={control}
                    name="service_date_of_injury.accident_description"
                    label="Accident Description"
                    errors={errors}
                    advancedEdit={true}
                    onSave={(value) =>
                      handleFieldSave(
                        "service_date_of_injury.accident_description",
                        value.toString(),
                      )
                    }
                  />
                  <ControlledTextArea
                    control={control}
                    name="pre_existing_conditions_notes"
                    label="Pre-existing and Unrelated Conditions"
                    errors={errors}
                    advancedEdit={true}
                    onSave={(value) =>
                      handleFieldSave(
                        "pre_existing_conditions_notes",
                        value.toString(),
                      )
                    }
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FutureMedicalClinicianNotes;
